import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { useUpdateAssistantList } from '@/Domain/hooks/AI/useAssistantList.hook';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';

export const useIdentityKnowledgeBase = (knowledgeBase: KnowledgeBase) => {
  const queryClient = useQueryClient();
  const { scenario } = useParams<{ scenario?: string }>();
  const { updateAssistant } = useUpdateAssistantList();

  const form = useForm<KnowledgeBase>({
    defaultValues: {
      one_word_description: knowledgeBase.one_word_description || '',
      short_description: knowledgeBase.short_description || '',
      ai_commands: knowledgeBase.ai_commands || [],
    },
    shouldFocusError: false,
  });

  const mutation = useMutation<
    KnowledgeBase,
    unknown,
    Partial<KnowledgeBase>,
    { previous?: KnowledgeBase }
  >({
    mutationKey: ['knowledge_base'],
    onMutate: (updated) => {
      const previous = queryClient.getQueryData<KnowledgeBase>(['knowledge_base']);
      queryClient.setQueryData<KnowledgeBase>(['knowledge_base'], (prev) =>
        prev ? { ...prev, ...updated } : prev,
      );
      return { previous };
    },
    onError: (_error, _updated, context) => {
      if (context?.previous) queryClient.setQueryData(['knowledge_base'], context.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['knowledge_base'], data);
      if (scenario) updateAssistant(scenario, { name: data.one_word_description });
      void queryClient.invalidateQueries({ queryKey: [Endpoint.ai.assistant] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });
    },
  });

  useAutoSubmit(form, (data) => mutation.mutate(data as Partial<KnowledgeBase>));

  const promptValue = (form.watch('short_description') || '').trim();
  const commands = form.watch('ai_commands') || [];

  return { form, promptValue, mainPromptEmpty: promptValue.length === 0, commands };
};
